import { PlusOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Col, Flex, Form, Input, InputNumber, Modal, Row, Select, Switch, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ExportButton } from "@/components/list/ExportButton";
import { SearchFilter, StatusFilter, useSearchFilter, useStatusFilter } from "@/components/list/filters";
import { useListPage } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import type { Supplier } from "./common";

type SupplierInput = Schemas["SupplierInput"];
const STATUS_CHOICES = ["active", "archived", "all"] as const;
const METHODS = ["transfer", "cash", "giro", "other"] as const;

/** Pembelian > Supplier (Plan §6.18). */
export function Suppliers() {
  const { t, formatNumber } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const status = useStatusFilter(STATUS_CHOICES, "active");
  const search = useSearchFilter();
  const page = useListPage();
  const [creating, setCreating] = useState(false);

  const filters = { ...(status.value !== "all" ? { status: status.value } : {}), ...search.query };
  const list = useQuery({
    queryKey: ["suppliers", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/suppliers", { params: { query: { ...filters, ...page.query } } })),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("supplier.title")}
        </Typography.Title>
        {can("procurement.manage") && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
            {t("supplier.add")}
          </Button>
        )}
      </Flex>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <SearchFilter filter={search} placeholder={t("supplier.search")} width={300} />
        <StatusFilter
          filter={status}
          options={[
            { value: "active", label: t("supplier.status.active") },
            { value: "archived", label: t("supplier.status.archived") },
            { value: "all", label: t("list.allStatuses") },
          ]}
        />
        <div style={{ flex: 1 }} />
        <ExportButton
          path="/v1/suppliers/export"
          query={filters}
          filename={t("supplier.title")}
          columns={[
            { key: "code", label: t("supplier.code") },
            { key: "name", label: t("supplier.name") },
            { key: "contactName", label: t("supplier.contact") },
            { key: "phone", label: t("supplier.phone") },
            { key: "email", label: t("supplier.email") },
            { key: "address", label: t("supplier.address") },
            { key: "npwp", label: t("supplier.npwp") },
            { key: "pkp", label: t("supplier.pkp"), values: { true: "PKP", false: "—" } },
            { key: "paymentTermsDays", label: t("supplier.terms"), kind: "number" },
            { key: "paymentMethod", label: t("supplier.method"), values: Object.fromEntries(METHODS.map((m) => [m, t(`supplier.method.${m}`)])) },
            { key: "bankName", label: t("supplier.bank") },
            { key: "bankAccountNo", label: t("supplier.bankAccountNo") },
            { key: "bankAccountName", label: t("supplier.bankAccountName") },
            { key: "openOrders", label: t("supplier.openOrders"), kind: "number" },
            { key: "status", label: t("common.status"), values: { active: t("supplier.status.active"), archived: t("supplier.status.archived") } },
          ]}
        />
      </Flex>
      <Table<Supplier>
        rowKey="id"
        loading={list.isFetching}
        dataSource={list.data?.items}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: t("supplier.empty") }}
        onRow={(s) => ({ onClick: () => navigate(`/app/procurement/suppliers/${s.id}`), style: { cursor: "pointer" } })}
        columns={[
          { title: t("supplier.code"), dataIndex: "code", width: 120, render: (v: string) => <span className="z-num">{v}</span> },
          {
            title: t("supplier.name"),
            dataIndex: "name",
            render: (v: string, s) => (
              <Flex gap={8} align="center">
                <Typography.Text strong>{v}</Typography.Text>
                {s.pkp && <Tag>PKP</Tag>}
                {s.status === "archived" && <Tag>{t("supplier.status.archived")}</Tag>}
              </Flex>
            ),
          },
          { title: t("supplier.contact"), render: (_, s) => [s.contactName, s.phone].filter(Boolean).join(" · ") || "—" },
          { title: t("supplier.terms"), dataIndex: "paymentTermsDays", width: 150, render: (v: number) => (v === 0 ? t("po.cod") : t("po.paymentTermsValue", { n: v })) },
          { title: t("supplier.openOrders"), dataIndex: "openOrders", width: 110, align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
        ]}
      />
      <SupplierForm open={creating} onClose={(id) => { setCreating(false); if (id) navigate(`/app/procurement/suppliers/${id}`); }} />
    </>
  );
}

/** Add or edit a supplier. */
export function SupplierForm({ open, supplier, onClose }: { open: boolean; supplier?: Supplier; onClose: (savedId?: string) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<SupplierInput>();
  const save = useMutation({
    mutationFn: (v: SupplierInput) => {
      const body: SupplierInput = { ...v, paymentTermsDays: v.paymentTermsDays ?? 0, pkp: !!v.pkp };
      return supplier
        ? unwrap(api.PUT("/v1/suppliers/{supplierID}", { params: { path: { supplierID: supplier.id } }, body: { ...body, code: undefined, status: supplier.status } }))
        : unwrap(api.POST("/v1/suppliers", { body: { ...body, code: v.code?.trim() || undefined } }));
    },
    onSuccess: (s) => {
      void message.success(t("supplier.saved"));
      void queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      onClose(s.id);
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal
      open={open}
      width={760}
      title={supplier ? t("supplier.edit") : t("supplier.add")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={() => onClose()}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={supplier ?? { paymentTermsDays: 0, paymentMethod: "transfer", pkp: false }}
        onFinish={(v) => save.mutate(v)}
      >
        <Row gutter={12}>
          {!supplier && (
            <Col xs={24} sm={8}>
              <Form.Item name="code" label={t("supplier.code")} tooltip={t("supplier.codeHelp")} normalize={(v: string) => v?.toUpperCase()}>
                <Input maxLength={32} className="z-num" />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} sm={supplier ? 24 : 16}>
            <Form.Item name="name" label={t("supplier.name")} rules={[{ required: true, min: 2, message: t("field.required") }]}>
              <Input maxLength={160} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="contactName" label={t("supplier.contact")}>
              <Input maxLength={160} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="phone" label={t("supplier.phone")}>
              <Input maxLength={30} inputMode="tel" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="email" label={t("supplier.email")} rules={[{ type: "email", message: t("field.invalid") }]}>
              <Input maxLength={160} inputMode="email" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="address" label={t("supplier.address")}>
              <Input.TextArea rows={2} maxLength={1000} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="npwp" label={t("supplier.npwp")}>
              <Input maxLength={40} className="z-num" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="pkp" label={t("supplier.pkp")} tooltip={t("supplier.pkpHelp")} valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="paymentTermsDays" label={t("supplier.terms")} tooltip={t("supplier.termsHelp")}>
              <InputNumber min={0} max={365} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item name="paymentMethod" label={t("supplier.method")}>
              <Select options={METHODS.map((m) => ({ value: m, label: t(`supplier.method.${m}`) }))} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item name="bankName" label={t("supplier.bank")}>
              <Input maxLength={160} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item name="bankAccountNo" label={t("supplier.bankAccountNo")}>
              <Input maxLength={160} className="z-num" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item name="bankAccountName" label={t("supplier.bankAccountName")}>
              <Input maxLength={160} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="note" label={t("supplier.note")}>
              <Input.TextArea rows={2} maxLength={1000} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
